// =============================================================================
// Origin UI — src/config/routes.tsx
// Application route definitions using React Router v6.
// Home screen renders full-width (no sidebar).
// All other pages render inside AppLayout (with sidebar).
//
// Phase 4 update: Employee routes added.
// Phase 6 update: Leave management routes added.
// Phase 7 update: Evaluation routes added.
// Onboarding Lifecycle: Offer management routes added.
// CreditCardAdmin: CC admin route now allows HRAdmin OR CreditCardAdmin.
// CM-6 update: Compliance routes added (landing all-staff; dashboard self-guards on role).
// Fix 2: Request creation/editing restricted to HR Admin (requests/new, requests/:id/edit).
// =============================================================================

import { createBrowserRouter } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { ModuleRoute } from '@/components/ModuleRoute';

// ─── Home / Dashboard (Phase 5) — full-width, no sidebar ───
import { HomePage } from '@/pages/home/HomePage';

// ─── Requester pages ───
import { MyRequestsPage } from '@/pages/requests/MyRequestsPage';
import { RequestFormPage } from '@/pages/requests/RequestFormPage';
import { RequestDetailPage } from '@/pages/requests/RequestDetailPage';

// ─── Approver pages ───
import { ApprovalsInboxPage } from '@/pages/approvals/ApprovalsInboxPage';
import { ApprovalReviewPage } from '@/pages/approvals/ApprovalReviewPage';
import { LeaveApprovalReviewPage } from '@/pages/approvals/LeaveApprovalReviewPage';

// ─── IT Ops pages ───
import { TasksQueuePage } from '@/pages/tasks/TasksQueuePage';
import { TaskDetailPage } from '@/pages/tasks/TaskDetailPage';
import { ITAssetManagementPage } from '@/pages/assets/ITAssetManagementPage';
import { AssetReportsPage } from '@/pages/assets/AssetReportsPage';

// ─── Employee pages (Phase 4) ───
import { EmployeeListPage } from '@/pages/employees/EmployeeListPage';
import { EmployeeProfilePage } from '@/pages/employees/EmployeeProfilePage';
import { MyProfileRedirect } from '@/pages/employees/MyProfileRedirect';
import { TeamViewPage } from '@/pages/employees/TeamViewPage';

// ─── Offer Management (Onboarding Lifecycle) ───
import { OffersPage } from '@/pages/offers/OffersPage';
import { OfferDetailPage } from '@/pages/offers/OfferDetailPage';

// ─── Leave Management pages (Phase 6) ───
import { MyLeavePage } from '@/pages/leave/MyLeavePage';
import { LeaveRequestPage } from '@/pages/leave/LeaveRequestPage';
import { TeamCalendarPage } from '@/pages/leave/TeamCalendarPage';
import { LeaveReportsPage } from '@/pages/leave/LeaveReportsPage';

// ─── Evaluation pages (Phase 7) ───
import { MyEvaluationPage } from '@/pages/evaluations/MyEvaluationPage';
import { TeamEvaluationsPage } from '@/pages/evaluations/TeamEvaluationsPage';
import { EvaluationFormPage } from '@/pages/evaluations/EvaluationFormPage';
import { EvaluationReportsPage } from '@/pages/evaluations/EvaluationReportsPage';

// ─── Credit Card pages ───
import { MyCardPage } from '@/pages/creditcards/MyCardPage';
import { CreditCardApprovalsPage } from '@/pages/creditcards/CreditCardApprovalsPage';
import { CreditCardAdminPage } from '@/pages/creditcards/CreditCardAdminPage';
import { CreditCardReportsPage } from "@/pages/creditcards/CreditCardReportsPage";

// ─── Reports page ───
import { ReportsPage } from '@/pages/reports/ReportsPage';

// ─── Compliance pages (CM-6) ───
import { CompliancePage } from '@/pages/compliance/CompliancePage';
import { ComplianceDashboard } from '@/pages/compliance/ComplianceDashboard';
import { AttestationFlow } from '@/pages/compliance/AttestationFlow';
import { DeclarationForm } from '@/pages/compliance/DeclarationForm';
import { ComplianceContentAdmin } from '@/pages/compliance/admin/ComplianceContentAdmin';
import { ComplianceArticlesAdmin } from '@/pages/compliance/admin/ComplianceArticlesAdmin';

// ─── Admin page ───
import { AdminConfigPage } from '@/pages/admin/AdminConfigPage';
import { DocumentExpiryReportPage } from '@/pages/reports/DocumentExpiryReportPage';

/**
 * Application router.
 * Home page is full-width (no sidebar) — it IS the navigation.
 * All other pages use AppLayout (sidebar + content area).
 */
export const router = createBrowserRouter([
  // ─── Home screen: full-width, no sidebar ───
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <HomePage />
      </ProtectedRoute>
    ),
  },

  // ─── All other pages: sidebar layout ───
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      // Requester routes — listing/detail open to any authenticated user;
      // creation and editing restricted to HR Admin (fix 2).
      { path: 'requests', element: <ModuleRoute module='onboarding'><MyRequestsPage /></ModuleRoute> },
      { path: 'requests/new', element: <ModuleRoute module='onboarding'><ProtectedRoute requiredRoles={['HRAdmin']}><RequestFormPage /></ProtectedRoute></ModuleRoute> },
      { path: 'requests/:id', element: <ModuleRoute module='onboarding'><RequestDetailPage /></ModuleRoute> },
      { path: 'requests/:id/edit', element: <ModuleRoute module='onboarding'><ProtectedRoute requiredRoles={['HRAdmin']}><RequestFormPage /></ProtectedRoute></ModuleRoute> },

      // Approver routes
      { path: 'approvals', element: <ApprovalsInboxPage /> },
      { path: 'approvals/leave/:id', element: <LeaveApprovalReviewPage /> },
      { path: 'approvals/:id', element: <ApprovalReviewPage /> },

      // IT Ops routes — visible to ITOps and HRAdmin
      {
        path: 'tasks',
        element: (
          <ProtectedRoute requiredRoles={['ITOps', 'HRAdmin']}>
            <TasksQueuePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tasks/:id',
        element: (
          <ProtectedRoute requiredRoles={['ITOps', 'HRAdmin']}>
            <TaskDetailPage />
          </ProtectedRoute>
        ),
      },

      // IT Asset Management (Task 4.2) — ITOps and HRAdmin
      {
        path: 'it-assets',
        element: (
          <ProtectedRoute requiredRoles={['ITOps', 'HRAdmin']}>
            <ITAssetManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'it-assets/reports',
        element: (
          <ProtectedRoute requiredRoles={['ITOps', 'HRAdmin']}>
            <AssetReportsPage />
          </ProtectedRoute>
        ),
      },

      // Employee routes (Phase 4) — HR Admin and OQTMan (read-only) see the list
      {
        path: 'employees',
        element: (
          <ProtectedRoute requiredRoles={['HRAdmin', 'OQTMan']}>
            <EmployeeListPage />
          </ProtectedRoute>
        ),
      },
      // Employee profile — HR, OQTMan, or Self (backend enforces)
      {
        path: 'employees/:id',
        element: <EmployeeProfilePage />,
      },

      // My profile shortcut — redirects to own employee record
      { path: 'profile', element: <MyProfileRedirect /> },

      // Team view — org hierarchy
      { path: 'team', element: <TeamViewPage /> },

      // Offer Management (Onboarding Lifecycle) — HR Admin only
      {
        path: 'offers',
        element: (
          <ProtectedRoute requiredRoles={['HRAdmin']}>
            <OffersPage />
          </ProtectedRoute>
        ),
      },
        { path: 'offers/new', element: <OfferDetailPage /> },
        { path: 'offers/:id', element: <OfferDetailPage /> },

      // Leave Management routes (Phase 6) — all authenticated users
      { path: 'leave', element: <MyLeavePage /> },
      { path: 'leave/request', element: <LeaveRequestPage /> },
      { path: 'leave/requests/:id', element: <LeaveRequestPage /> },
      { path: 'leave/team', element: <TeamCalendarPage /> },
      { path: 'leave/reports', element: <LeaveReportsPage /> },
        { path: 'reports/document-expiry', element: <DocumentExpiryReportPage /> },

      // Evaluation routes (Phase 7) — all authenticated users
      { path: 'evaluations/mine', element: <ModuleRoute module='appraisals'><MyEvaluationPage /></ModuleRoute> },
      { path: 'evaluations/team', element: <ModuleRoute module='appraisals'><TeamEvaluationsPage /></ModuleRoute> },
      { path: 'evaluations/:id', element: <ModuleRoute module='appraisals'><EvaluationFormPage /></ModuleRoute> },
      {
        path: 'evaluations/reports',
        element: (
          <ProtectedRoute requiredRoles={['HRAdmin']}>
            <EvaluationReportsPage />
          </ProtectedRoute>
        ),
      },

      // Credit Card routes
      { path: 'credit-cards/my-card', element: <ModuleRoute module='creditCards'><MyCardPage /></ModuleRoute> },
      { path: 'credit-cards/approvals', element: <ModuleRoute module='creditCards'><CreditCardApprovalsPage /></ModuleRoute> },
      {
        path: 'credit-cards/admin',
        element: (
          <ModuleRoute module='creditCards'>
            <ProtectedRoute requiredRoles={['HRAdmin', 'CreditCardAdmin']}>
              <CreditCardAdminPage />
            </ProtectedRoute>
          </ModuleRoute>
        ),
      },

      { path: "credit-cards/reports", element: <ModuleRoute module="creditCards"><ProtectedRoute requiredRoles={["HRAdmin", "CreditCardAdmin"]}><CreditCardReportsPage /></ProtectedRoute></ModuleRoute> },
      // Compliance routes (CM-6) — gated on the compliance module flag;
      // the dashboard self-guards on the Compliance role inside the component.
      { path: 'compliance', element: <ModuleRoute module='compliance'><CompliancePage /></ModuleRoute> },
      { path: 'compliance/dashboard', element: <ModuleRoute module='compliance'><ComplianceDashboard /></ModuleRoute> },
      { path: 'compliance/attest/:itemId', element: <ModuleRoute module='compliance'><AttestationFlow /></ModuleRoute> },
      { path: 'compliance/declaration/:itemId', element: <ModuleRoute module='compliance'><DeclarationForm /></ModuleRoute> },
      { path: 'compliance/admin/content', element: <ModuleRoute module='compliance'><ComplianceContentAdmin /></ModuleRoute> },
      { path: 'compliance/admin/articles', element: <ModuleRoute module='compliance'><ComplianceArticlesAdmin /></ModuleRoute> },

      // Reports — HRAdmin only (Auditor role removed, HR gets reporting)
      {
        path: 'reports',
        element: (
          <ProtectedRoute requiredRoles={['HRAdmin']}>
            <ReportsPage />
          </ProtectedRoute>
        ),
      },

      // Admin Config — HRAdmin or SysAdmin (ITOps for the Onboarding category only)
      {
        path: 'admin',
        element: (
          <ProtectedRoute requiredRoles={['HRAdmin', 'SysAdmin', 'ITOps', 'CreditCardAdmin']}>
            <AdminConfigPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
]);
